import { useCallback, useMemo } from 'react'
import { usePresence } from '@/contexts/PresenceContext'
import type { RecoverRequest } from '../api'
import { accessRequirement, rankCandidates, type Candidate, type PresenceState, type Workforce, type AccessRequirement } from './logic'

/**
 * Candidates for one object, ranked, with live presence folded in.
 *
 * One hook rather than two call sites, because the selection rail and the assign dialog must agree
 * about who is first and why. If they ranked separately, an engineer could sit top of the rail and
 * third in the dialog opened from that same row.
 */
export function useCandidates(row: RecoverRequest | null, workforce: Workforce | undefined): {
  candidates: Candidate[]
  access: AccessRequirement
  eligibleCount: number
  poolCount: number
} {
  const presence = usePresence()

  const presenceOf = useCallback((id: number): PresenceState => {
    const s = presence?.getUserStatus?.(id)
    return (s ?? 'offline') as PresenceState
  }, [presence])

  return useMemo(() => {
    const { access } = accessRequirement(row ?? {}, workforce?.accessByObject)
    const engineers = workforce?.engineers ?? []
    if (!row || engineers.length === 0) {
      return { candidates: [], access, eligibleCount: 0, poolCount: engineers.length }
    }

    const siteCounts = new Map<number, number>()
    for (const e of engineers) {
      if (e.locationId == null) continue
      siteCounts.set(e.locationId, (siteCounts.get(e.locationId) ?? 0) + 1)
    }

    const candidates = rankCandidates({
      engineers,
      presenceOf,
      objectLocationId: row.locationId ?? null,
      objectTeamId: row.responsibleTeamId ?? null,
      access,
      runbookMinutes: null,
      siteEngineerCount: (id) => (id == null ? 0 : siteCounts.get(id) ?? 0),
      // Distance is only ever offered where BOTH sites have coordinates; the endpoint already
      // refuses to guess one, so a null here stays null all the way to the card.
      distanceKm: (fromId) => {
        if (fromId == null || row.locationId == null) return null
        const site = workforce?.sites.find((s) => s.locationId === row.locationId)
        return site?.nearestCover?.locationId === fromId ? site.nearestCover.distanceKm : null
      },
    })

    return {
      candidates,
      access,
      eligibleCount: candidates.filter((c) => !c.ineligible).length,
      poolCount: engineers.length,
    }
  }, [row, workforce, presenceOf])
}
